import { useState, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import { apiGet, apiPost, getApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from './Button';
import { Modal } from './Modal';
import { Input } from './Input';
import { useToast } from './Toast';

type EmailResult = { stubbed: boolean; to: string };

interface Props {
  /** API endpoint. For `method="post"` it's hit with `{ to }`; for `method="get"`
   *  it's hit with `?deliver=email&to=…` plus any `params`. */
  endpoint: string;
  /** Default/suggested recipient (the customer's email on file). */
  defaultEmail?: string | null;
  /** Internal documents (vouchers, reports) default to the logged-in user — a
   *  blank recipient is allowed and the server falls back to your own email. */
  selfDefault?: boolean;
  method?: 'post' | 'get';
  /** Extra query params for `method="get"` (e.g. month, groupBy, format, from, to). */
  params?: Record<string, string>;
  /** Modal title, e.g. "Email quotation". */
  title: string;
  /** One-line description under the title. */
  subtitle?: string;
  buttonLabel?: string;
  buttonVariant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  buttonSize?: 'sm' | 'md' | 'lg';
  /** Custom trigger (e.g. a compact text link for table rows). Receives an
   *  `open` callback; when provided it replaces the default button. */
  renderTrigger?: (open: () => void) => ReactNode;
}

// Button + modal to email a downloadable document to a recipient. The server
// renders the PDF/XLSX and sends it via Resend (stubbed in dev without a key).
export function EmailDocButton({
  endpoint,
  defaultEmail,
  selfDefault = false,
  method = 'post',
  params,
  title,
  subtitle,
  buttonLabel = 'Email',
  buttonVariant = 'ghost',
  buttonSize = 'md',
  renderTrigger,
}: Props): JSX.Element {
  const { user } = useAuth();
  const toast = useToast();
  const initial = defaultEmail ?? (selfDefault ? (user?.email ?? '') : '');
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState(initial);

  const send = useMutation({
    mutationFn: () => {
      const trimmed = to.trim() || undefined;
      return method === 'get'
        ? apiGet<EmailResult>(endpoint, { ...params, deliver: 'email', to: trimmed })
        : apiPost<EmailResult>(endpoint, { to: trimmed });
    },
    onSuccess: (r) => {
      setOpen(false);
      toast.success(
        r.stubbed
          ? `Email logged (no email provider configured) — would send to ${r.to}`
          : `Sent to ${r.to}`,
      );
    },
  });

  // A recipient is required only when there's no automatic fallback.
  const canSend = selfDefault || to.trim().length > 0;

  const openModal = (): void => {
    send.reset();
    setTo(initial);
    setOpen(true);
  };

  return (
    <>
      {renderTrigger ? (
        renderTrigger(openModal)
      ) : (
        <Button variant={buttonVariant} size={buttonSize} onClick={openModal}>
          {buttonLabel}
        </Button>
      )}
      {open && (
        <Modal
          title={title}
          subtitle={subtitle}
          onClose={() => setOpen(false)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setOpen(false)}>
                Close
              </Button>
              <Button loading={send.isPending} disabled={!canSend} onClick={() => send.mutate()}>
                Send
              </Button>
            </>
          }
        >
          <div className="space-y-3">
            <Input
              label="Recipient email"
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder={
                selfDefault ? (user?.email ?? 'you@example.com') : 'customer@example.com'
              }
            />
            {selfDefault && (
              <div className="text-xs text-ink-3">Leave blank to send to your own email.</div>
            )}
            {!selfDefault && !defaultEmail && (
              <div className="text-xs text-warning">
                No email on file — enter a recipient address to send.
              </div>
            )}
            {send.isError && (
              <div className="text-xs text-negative">{getApiError(send.error).message}</div>
            )}
          </div>
        </Modal>
      )}
    </>
  );
}
